/**
 * What a story is told from: a record's documents through all its runs (its card's messages), or one
 * run's documents (its execution's items). A run's story follows its first document's record key back to
 * that record's card, so the whole pipeline is told, not one hop. Ported from pages/Data.tsx (loadJourney).
 */
import { Api, type Card, type RecordKind, type Run } from '../lib/api.ts';
import { stripSystem } from '../lib/systems.ts';
import { noun } from '../design/primitives.tsx';
import type { Journey, PlayDoc } from '../story/engine.ts';
import { quoted } from './scope.ts';

export type JourneyRef =
  | { kind: 'record'; cls: string; key: string }
  | { kind: 'run'; executionId: string; run?: Run | null };

const named = (cls: string, key: string) => `${noun(cls)} ${key}`;
const docsOf = (c: Card): PlayDoc[] =>
  c.messages.map((m) => ({ cls: m.cls, type: m.type, at: m.at, executionId: m.executionId, process: m.process, status: m.status, info: m.info, ref: m }));

/** A record's documents: its card, with the stage's time params. */
async function ofCard(tid: string, time: URLSearchParams, key: string, cls: string): Promise<PlayDoc[]> {
  const p = new URLSearchParams(time);
  p.set('q', quoted(key));
  p.set('focus', cls);
  return docsOf(await Api.card(tid, p));
}

export async function loadJourney(tid: string, time: URLSearchParams, kinds: RecordKind[], ref: JourneyRef): Promise<Journey> {
  if (ref.kind === 'record') {
    const docs = await ofCard(tid, time, ref.key, ref.cls);
    return { kind: 'record', title: `The story of ${named(ref.cls, ref.key)}`, docs };
  }
  const ex = await Api.execution(tid, ref.executionId);
  const items = ex.steps.flatMap((st) => st.items.map((h) => ({ h, st }))).sort((a, b) => a.h.timestamp - b.h.timestamp);
  const process = ref.run?.process ?? items.find((x) => x.h.meta?.process)?.h.meta.process ?? null;
  const docs: PlayDoc[] = items.map(({ h, st }) => ({
    cls: h.messageClass, type: h.type, at: h.timestamp, executionId: ref.executionId, process,
    status: st.status ?? null, info: h.meta?.info ?? st.info ?? null, ref: h,
  }));
  const runName = process ? stripSystem(process) : 'an integration';
  // The whole pipeline, by the identifier of the run's first document
  for (const { h } of items.slice(0, 2)) {
    const k = kinds.find((x) => x.cls === h.messageClass);
    if (!k) continue;
    const m = await Api.message(tid, h.vaultId, h.uuid, h.day);
    const key = m.fields.find(([path]) => k.key.includes(path))?.[1];
    if (!key) continue;
    const all = await ofCard(tid, time, key, k.cls);
    if (all.some((d) => d.executionId === ref.executionId)) {
      return { kind: 'run', title: `The story of ${named(k.cls, key)}, from a run of ${runName}`, docs: all };
    }
  }
  return { kind: 'run', title: `A run of ${runName}`, docs };
}
